<template>
  <div class="px-4 py-2 w-full">
    <div class="flex items-baseline">
      <b-tabs v-model="activeTab" class="statistics-tabs" type="is-toggle">
        <b-tab-item
          :value="TAB_TYPES.PENDING"
          :label="`尚未建立 (${pendingList.length || 0})`" />
        <b-tab-item
          :value="TAB_TYPES.CREATED"
          :label="`已建立 (${createdList.length || 0})`" />
      </b-tabs>
      <span class="ml-4 text-gray-500 text-sm">為確保排程正常，訂購列表需要至少一個訂購項目。</span>
    </div>
    <b-table
      v-if="activeTab === TAB_TYPES.PENDING"
      :data="pendingListPerPage"
      :default-sort="[sortField, sortOrder]"
      :loading="loading"
      backend-sorting
      scrollable
      @sort="onSort"
    >
      <b-table-column
        :width="28"
        field="id"
        header-class="font-medium text-xs"
        label="No."
        v-slot="props"
      >
        <span class="text-sm">{{ props.index + 1 }}.</span>
      </b-table-column>
      <b-table-column
        :width="268"
        field="create"
        header-class="font-medium text-xs"
        label="公告時間"
        sortable
        v-slot="props"
      >
        <w-date-time-display
          v-if="props.row.created_at"
          :date-time="props.row.created_at"
        />
      </b-table-column>
      <b-table-column
        :width="300"
        field="start"
        header-class="font-medium text-xs"
        label="開放訂購時段"
        v-slot="props"
      >
        <w-date-time-display :date-time="props.row" />
      </b-table-column>
      <b-table-column
        :width="300"
        field="receive"
        header-class="font-medium text-xs"
        label="領取時段"
        v-slot="props"
      >
        <div class="flex gap-4 items-center">
          <w-date-time-display
            v-if="props.row.receive_times?.length"
            :date-time="props.row.receive_times?.[0]"
          />
          <b-dropdown
            v-if="props.row.receive_times?.length > 1"
            append-to-body
            aria-role="list"
            max-height="200"
            position="is-bottom-left"
            scrollable
          >
            <template #trigger>
              <i class="cursor-pointer icon-down-chevron text-base" />
            </template>
            <b-dropdown-item
              v-for="(receiveTime, index) in extraReceiveTimes(props.row.receive_times)"
              :key="`receiveTime-${props.row.id}-${index}`"
              :focusable="false"
              aria-role="listitem"
              class="w-max"
              custom
            >
              <w-date-time-display :date-time="receiveTime" />
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-table-column>
      <b-table-column
        v-if="!viewMode"
        :width="56"
        field="delete"
        header-class="font-medium text-xs"
        label="刪除"
        v-slot="props"
      >
        <w-button
          :disabled="pendingList.length <= 1"
          class="hover:text-red-700 text-red-500"
          type="icon"
          @click="onScheduleDelete(props.row.receive_times)"
        >
          <i class="icon-trash text-base" />
        </w-button>
      </b-table-column>
      <template #empty>
        <p class="my-12 text-center text-gray-700 text-sm">無尚未建立的訂購</p>
      </template>
    </b-table>
    <b-table
      v-else
      :data="createdListPerPage"
      :default-sort="[sortField, sortOrder]"
      :loading="loading"
      backend-sorting
      scrollable
      @sort="onSort"
    >
      <b-table-column
        :width="28"
        field="id"
        header-class="font-medium text-xs"
        label="No."
        v-slot="props"
      >
        <span class="text-sm">{{ props.index + 1 }}.</span>
      </b-table-column>
      <b-table-column
        :width="268"
        field="create"
        header-class="font-medium text-xs"
        label="公告時間"
        sortable
        v-slot="props"
      >
        <w-date-time-display
          v-if="props.row.created_at"
          :date-time="props.row.created_at"
        />
      </b-table-column>
      <b-table-column
        :width="300"
        field="start"
        header-class="font-medium text-xs"
        label="開放訂購時段"
        sortable
        v-slot="props"
      >
        <w-date-time-display :date-time="props.row" />
      </b-table-column>
      <b-table-column
        :width="300"
        field="receive"
        header-class="font-medium text-xs"
        label="領取時段"
        v-slot="props"
      >
        <div class="flex gap-4 items-center">
          <w-date-time-display
            v-if="props.row.receive_times?.length"
            :date-time="props.row.receive_times?.[0]"
          />
          <b-dropdown
            v-if="props.row.receive_times?.length > 1"
            append-to-body
            aria-role="list"
            max-height="200"
            position="is-bottom-left"
            scrollable
          >
            <template #trigger>
              <i class="cursor-pointer icon-down-chevron text-base" />
            </template>
            <b-dropdown-item
              v-for="(receiveTime, index) in extraReceiveTimes(props.row.receive_times)"
              :key="`receiveTime-${props.row.id}-${index}`"
              :focusable="false"
              aria-role="listitem"
              class="w-max"
              custom
            >
              <w-date-time-display :date-time="receiveTime" />
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-table-column>
      <b-table-column
        :width="68"
        field="id"
        header-class="font-medium text-xs"
        label="訂購ID"
        v-slot="props"
      >
        <span class="text-sm">{{ props.row.id }}</span>
      </b-table-column>
      <b-table-column
        :width="100"
        field="status"
        header-class="font-medium text-xs"
        label="狀態"
        v-slot="props"
      >
        <span :class="[getStatusClass(props.row.status)]" class="text-sm">
          {{ getStatusText(props.row.status) }}
        </span>
      </b-table-column>
      <template #empty>
        <p class="my-12 text-center text-gray-700 text-sm">無已建立的訂購</p>
      </template>
    </b-table>
    <div
      v-if="activeTab === TAB_TYPES.PENDING ? pendingList.length : createdList.length"
      class="flex gap-4 items-center">
      <b-select
        v-model="perPage"
      >
        <option
          v-for="option in perPageOptions"
          :value="option.value"
          :key="option.label"
        >
          {{ option.label }}
        </option>
      </b-select>
      <div class="flex-grow">
        <b-pagination
          :total="activeTab === TAB_TYPES.PENDING ? pendingList.length : createdList.length"
          v-model="currentPage"
          range-before="3"
          range-after="1"
          nextIcon="right-chevron"
          prevIcon="left-chevron"
          :per-page="perPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ScheduleOrderList from './ScheduleOrderList';

export default ScheduleOrderList;
</script>

<style lang="scss" scoped>
@import 'ScheduleOrderList';
</style>
